import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import {
    useSearchParams,
} from "react-router-dom";

import {
    MdClose,
    MdDelete,
    MdRestaurantMenu,
} from "react-icons/md";

import API from "../../../services/api";
import socket from "../../../services/socket";
import getImageUrl from "../../../utils/imageUrl";
import ConversationList from "../../../components/customer/Messaging/ConversationList";
import ChatHeader from "../../../components/customer/Messaging/ChatHeader";
import Message from "../../../components/customer/Messaging/Message";
import MessageInput from "../../../components/customer/Messaging/MessageInput";

import AddMenuModal from "./AddMenuModal";

import "./ProviderMessages.css";


function ProviderMessages() {

    // =========================================================
    // URL
    // =========================================================

    const [searchParams] =
        useSearchParams();


    // =========================================================
    // CONVERSATIONS
    // =========================================================

    const [
        conversations,
        setConversations,
    ] = useState([]);


    const [
        selectedConversation,
        setSelectedConversation,
    ] = useState(null);


    const [
        messages,
        setMessages,
    ] = useState([]);


    const [
        loading,
        setLoading,
    ] = useState(true);


    const [
        loadingMessages,
        setLoadingMessages,
    ] = useState(false);


    const [
        error,
        setError,
    ] = useState("");


    const [
        mobileChatOpen,
        setMobileChatOpen,
    ] = useState(false);


    // =========================================================
    // ADD MENU
    // =========================================================

    const [
        showAddMenu,
        setShowAddMenu,
    ] = useState(false);


    const [
        savingMenu,
        setSavingMenu,
    ] = useState(false);


    // =========================================================
    // MY MENU
    // =========================================================

    const [
        showMyMenu,
        setShowMyMenu,
    ] = useState(false);


    const [
        myMenu,
        setMyMenu,
    ] = useState([]);


    const [
        loadingMyMenu,
        setLoadingMyMenu,
    ] = useState(false);


    const [
        myMenuError,
        setMyMenuError,
    ] = useState("");


    const [
        deletingMenuId,
        setDeletingMenuId,
    ] = useState(null);


    // =========================================================
    // CURRENT PROVIDER
    // =========================================================

    const getCurrentUser =
        () => {

            try {

                const storedUser =
                    localStorage.getItem(
                        "user"
                    );


                if (!storedUser) {
                    return null;
                }


                return JSON.parse(
                    storedUser
                );

            } catch (error) {

                console.error(
                    "Provider User Parse Error:",
                    error
                );

                return null;
            }

        };


    const currentUser =
        getCurrentUser();


    const currentUserId =
        currentUser?._id ||
        currentUser?.id;


    // =========================================================
    // MESSAGE SCROLL
    // =========================================================

    const messagesEndRef =
        useRef(null);


    const scrollToBottom =
        () => {

            messagesEndRef.current?.scrollIntoView({
                behavior: "smooth",
            });

        };


    // =========================================================
    // GET PROVIDER ID
    // =========================================================

    const getProviderId =
        (conversation) => {

            if (!conversation) {
                return null;
            }


            return (
                conversation.providerId?._id ||
                conversation.providerId
            );

        };


    // =========================================================
    // LOAD CONVERSATIONS
    // =========================================================

    const loadConversations =
        useCallback(
            async () => {

                try {

                    setLoading(true);

                    setError("");


                    const response =
                        await API.get(
                            "/conversations/provider"
                        );


                    if (
                        response.data?.success
                    ) {

                        const data =
                            response.data.conversations ||
                            [];


                        setConversations(
                            data
                        );


                        return data;
                    }


                    return [];

                } catch (error) {

                    console.error(
                        "Provider Conversations Error:",
                        error
                    );


                    setError(
                        error.response?.data?.message ||
                        "Failed to load conversations"
                    );


                    return [];

                } finally {

                    setLoading(false);

                }

            },
            []
        );


    // =========================================================
    // LOAD MESSAGES
    // =========================================================

    const loadMessages =
        useCallback(
            async (
                conversationId
            ) => {

                if (!conversationId) {
                    return;
                }


                try {

                    setLoadingMessages(
                        true
                    );


                    const response =
                        await API.get(
                            `/messages/${conversationId}`
                        );


                    if (
                        response.data?.success
                    ) {

                        setMessages(
                            response.data.messages ||
                            []
                        );

                    } else {

                        setMessages([]);

                    }

                } catch (error) {

                    console.error(
                        "Provider Messages Error:",
                        error
                    );


                    setMessages([]);

                } finally {

                    setLoadingMessages(
                        false
                    );

                }

            },
            []
        );


    // =========================================================
    // MARK CONVERSATION READ
    // =========================================================

    const markConversationRead =
        useCallback(
            async (
                conversationId
            ) => {

                if (!conversationId) {
                    return;
                }


                try {

                    await API.patch(
                        `/messages/${conversationId}/read`
                    );


                    setConversations(
                        previous =>
                            previous.map(
                                conversation => {

                                    if (
                                        conversation._id !==
                                        conversationId
                                    ) {

                                        return conversation;

                                    }


                                    return {
                                        ...conversation,
                                        unreadProvider: 0,
                                    };

                                }
                            )
                    );


                    setSelectedConversation(
                        previous => {

                            if (
                                !previous ||
                                previous._id !==
                                conversationId
                            ) {

                                return previous;

                            }


                            return {
                                ...previous,
                                unreadProvider: 0,
                            };

                        }
                    );

                } catch (error) {

                    console.error(
                        "Provider Mark Read Error:",
                        error
                    );

                }

            },
            []
        );


    // =========================================================
    // SELECT CONVERSATION
    // =========================================================

    const handleSelectConversation =
        useCallback(
            async (
                conversation
            ) => {

                if (!conversation) {
                    return;
                }


                setSelectedConversation(
                    conversation
                );


                setMobileChatOpen(
                    true
                );


                await loadMessages(
                    conversation._id
                );


                await markConversationRead(
                    conversation._id
                );

            },
            [
                loadMessages,
                markConversationRead,
            ]
        );


    // =========================================================
    // INITIAL LOAD
    // =========================================================

    useEffect(() => {

        const initialize =
            async () => {

                const loaded =
                    await loadConversations();


                const conversationId =
                    searchParams.get(
                        "conversation"
                    );


                // ---------------------------------------------
                // URL CONVERSATION
                // ---------------------------------------------

                if (
                    conversationId
                ) {

                    const conversation =
                        loaded.find(
                            item =>
                                item._id ===
                                conversationId
                        );


                    if (
                        conversation
                    ) {

                        await handleSelectConversation(
                            conversation
                        );


                        return;
                    }

                }


                // ---------------------------------------------
                // FIRST CONVERSATION
                // ---------------------------------------------

                if (
                    loaded.length > 0
                ) {

                    await handleSelectConversation(
                        loaded[0]
                    );

                }

            };


        initialize();

    }, [
        loadConversations,
        searchParams,
        handleSelectConversation,
    ]);


    // =========================================================
    // PROVIDER SOCKET REGISTRATION
    // =========================================================

    useEffect(() => {

        if (
            !conversations.length
        ) {
            return;
        }


        const providerId =
            getProviderId(
                conversations[0]
            );


        if (!providerId) {
            return;
        }


        const registerProvider =
            () => {

                console.log(
                    "🟢 Provider socket connected:",
                    socket.id
                );


                console.log(
                    "🍽 Registering Provider:",
                    providerId
                );


                socket.emit(
                    "register-provider",
                    providerId
                );

            };


        if (
            socket.connected
        ) {

            registerProvider();

        } else {

            socket.on(
                "connect",
                registerProvider
            );

        }


        return () => {

            socket.off(
                "connect",
                registerProvider
            );

        };

    }, [
        conversations,
    ]);


    // =========================================================
    // REAL-TIME MESSAGES
    // =========================================================

    useEffect(() => {

        const handleNewMessage =
            (
                incomingMessage
            ) => {

                console.log(
                    "📩 Provider New Message:",
                    incomingMessage
                );


                if (
                    !incomingMessage
                ) {
                    return;
                }


                const conversationId =
                    incomingMessage.conversationId;


                // ---------------------------------------------
                // CURRENT CONVERSATION
                // ---------------------------------------------

                if (
                    selectedConversation?._id ===
                    conversationId
                ) {

                    setMessages(
                        previous => {

                            const exists =
                                previous.some(
                                    message =>
                                        message._id ===
                                        incomingMessage._id
                                );


                            if (exists) {
                                return previous;
                            }


                            return [
                                ...previous,
                                incomingMessage,
                            ];

                        }
                    );


                    if (
                        incomingMessage.senderRole ===
                        "customer"
                    ) {

                        markConversationRead(
                            conversationId
                        );

                    }

                }


                // ---------------------------------------------
                // CONVERSATION PREVIEW
                // ---------------------------------------------

                setConversations(
                    previous =>
                        previous.map(
                            conversation => {

                                if (
                                    conversation._id !==
                                    conversationId
                                ) {

                                    return conversation;

                                }


                                const isCurrent =
                                    selectedConversation?._id ===
                                    conversationId;


                                return {

                                    ...conversation,

                                    lastMessage:
                                        incomingMessage.message ||
                                        conversation.lastMessage,

                                    lastMessageAt:
                                        incomingMessage.createdAt,

                                    lastSenderRole:
                                        incomingMessage.senderRole,

                                    unreadProvider:
                                        isCurrent
                                            ? 0
                                            : incomingMessage.senderRole ===
                                              "customer"
                                                ? (
                                                    conversation.unreadProvider ||
                                                    0
                                                ) + 1
                                                : conversation.unreadProvider,

                                };

                            }
                        )
                );

            };


        socket.on(
            "new-message",
            handleNewMessage
        );


        return () => {

            socket.off(
                "new-message",
                handleNewMessage
            );

        };

    }, [
        selectedConversation,
        markConversationRead,
    ]);


    // =========================================================
    // AUTO SCROLL
    // =========================================================

    useEffect(() => {

        if (
            !loadingMessages
        ) {

            const timer =
                setTimeout(
                    scrollToBottom,
                    50
                );


            return () =>
                clearTimeout(timer);

        }

    }, [
        messages,
        loadingMessages,
    ]);


    // =========================================================
    // SEND MESSAGE
    // =========================================================

    const handleSendMessage =
        async ({
            message,
            messageType = "text",
        }) => {

            if (
                !selectedConversation ||
                !message?.trim()
            ) {
                return;
            }


            try {

                const response =
                    await API.post(
                        `/messages/${selectedConversation._id}`,
                        {
                            message:
                                message.trim(),

                            messageType,
                        }
                    );


                if (
                    response.data?.success
                ) {

                    const newMessage =
                        response.data.data;


                    setMessages(
                        previous => {

                            const exists =
                                previous.some(
                                    item =>
                                        item._id ===
                                        newMessage._id
                                );


                            if (exists) {
                                return previous;
                            }


                            return [
                                ...previous,
                                newMessage,
                            ];

                        }
                    );


                    setConversations(
                        previous =>
                            previous.map(
                                conversation => {

                                    if (
                                        conversation._id !==
                                        selectedConversation._id
                                    ) {

                                        return conversation;

                                    }


                                    return {

                                        ...conversation,

                                        lastMessage:
                                            newMessage.message,

                                        lastMessageAt:
                                            newMessage.createdAt,

                                        lastSenderRole:
                                            "provider",

                                        unreadProvider: 0,

                                    };

                                }
                            )
                    );


                    setSelectedConversation(
                        previous => {

                            if (!previous) {
                                return previous;
                            }


                            return {

                                ...previous,

                                lastMessage:
                                    newMessage.message,

                                lastMessageAt:
                                    newMessage.createdAt,

                                lastSenderRole:
                                    "provider",

                                unreadProvider: 0,

                            };

                        }
                    );

                }

            } catch (error) {

                console.error(
                    "Provider Send Message Error:",
                    error
                );


                throw error;

            }

        };


    // =========================================================
    // BACK
    // =========================================================

    const handleBack =
        () => {

            setMobileChatOpen(
                false
            );

        };


    // =========================================================
    // PROVIDER CALL
    // =========================================================

    const handleCall =
        () => {

            const customer =
                selectedConversation?.customerId;


            const phone =
                customer?.phone;


            if (!phone) {

                console.log(
                    "Customer phone number unavailable"
                );

                return;
            }


            window.location.href =
                `tel:${phone}`;

        };


    // =========================================================
    // EMPTY CHAT
    // =========================================================

    const renderEmptyChat =
        () => {

            return (

                <div className="provider-messages-empty">

                    <div className="provider-messages-empty-icon">
                        💬
                    </div>


                    <h2>
                        Customer Messages
                    </h2>


                    <p>
                        Select a conversation
                        to chat with your
                        customers.
                    </p>

                </div>

            );

        };


    // =========================================================
    // OPEN ADD MENU
    // =========================================================

    const handleOpenMenu =
        () => {

            if (
                !selectedConversation
            ) {
                return;
            }


            setShowAddMenu(
                true
            );

        };


    // =========================================================
    // CLOSE ADD MENU
    // =========================================================

    const handleCloseMenu =
        () => {

            if (
                savingMenu
            ) {
                return;
            }


            setShowAddMenu(
                false
            );

        };


    // =========================================================
    // SAVE MENU ITEM
    // =========================================================

    const handleSaveMenu =
        async ({
            foodName,
            price,
        }) => {

            if (
                !foodName?.trim() ||
                !price ||
                price <= 0
            ) {

                return;

            }


            try {

                setSavingMenu(
                    true
                );


                const formData =
                    new FormData();


                formData.append(
                    "foodName",
                    foodName.trim()
                );


                formData.append(
                    "price",
                    Number(price)
                );

formData.append(
    "description",
    foodName.trim()
);

const response =
    await API.post(
        "/chat-menu/add",
        formData
    );
                if (
                    response.data?.success
                ) {

                    console.log(
    "✅ Chat Menu item saved:",
    response.data.menuItem
);

                    setShowAddMenu(
                        false
                    );


                    // If My Menu is already open,
                    // refresh it immediately.

                    if (
                        showMyMenu
                    ) {

                        await loadMyMenu();

                    }

                } else {

                    console.error(
                        "Menu save failed:",
                        response.data
                    );

                }

            } catch (error) {

                console.error(
                    "Save Menu Item Error:",
                    error
                );

            } finally {

                setSavingMenu(
                    false
                );

            }

        };


    // =========================================================
    // LOAD PROVIDER MY MENU
    // =========================================================

    const loadMyMenu =
        useCallback(
            async () => {

                try {

                    setLoadingMyMenu(
                        true
                    );


                    setMyMenuError("");


                   const response =
    await API.get(
        "/chat-menu/my-menu"
    );


                    console.log(
                        "📋 Provider foods response:",
                        response.data
                    );


                    if (
                        response.data?.success
                    ) {

                        const menuItems =
    response.data.menuItems || [];

setMyMenu(
    menuItems
);

                    } else {

                        setMyMenu([]);

                        setMyMenuError(
                            response.data?.message ||
                            "Unable to load your menu"
                        );

                    }

                } catch (error) {

                    console.error(
                        "Load Provider My Menu Error:",
                        error
                    );


                    setMyMenu([]);


                    setMyMenuError(
                        error.response?.data?.message ||
                        "Failed to load your menu"
                    );

                } finally {

                    setLoadingMyMenu(
                        false
                    );

                }

            },
            []
        );


    // =========================================================
    // OPEN MY MENU
    // =========================================================

    const handleOpenMyMenu =
        async () => {

            setShowMyMenu(
                true
            );


            await loadMyMenu();

        };


    // =========================================================
    // CLOSE MY MENU
    // =========================================================

    const handleCloseMyMenu =
        () => {

            if (
                deletingMenuId
            ) {
                return;
            }


            setShowMyMenu(
                false
            );


            setMyMenuError("");

        };


    // =========================================================
    // DELETE MENU ITEM
    // =========================================================

    const handleDeleteMenu =
        async (
            foodId
        ) => {

            if (!foodId) {
                return;
            }


            const confirmed =
                window.confirm(
                    "Are you sure you want to delete this menu item?"
                );


            if (!confirmed) {
                return;
            }


            try {

                setDeletingMenuId(
                    foodId
                );


                const response =
    await API.delete(
        `/chat-menu/${foodId}`
    );


                if (
                    response.data?.success
                ) {

                    console.log(
                        "🗑 Menu item deleted:",
                        foodId
                    );


                    setMyMenu(
                        previous =>
                            previous.filter(
                                item =>
                                    item._id !==
                                    foodId
                            )
                    );

                } else {

                    alert(
                        response.data?.message ||
                        "Failed to delete menu item"
                    );

                }

            } catch (error) {

                console.error(
                    "Delete Menu Item Error:",
                    error
                );


                alert(
                    error.response?.data?.message ||
                    "Failed to delete menu item"
                );

            } finally {

                setDeletingMenuId(
                    null
                );

            }

        };


    // =========================================================
    // RENDER MY MENU
    // =========================================================

    const renderMyMenu =
        () => {

            if (
                !showMyMenu
            ) {
                return null;
            }


            return (

                <div
                    className="provider-my-menu-overlay"
                    onMouseDown={
                        (event) => {

                            if (
                                event.target ===
                                event.currentTarget
                            ) {

                                handleCloseMyMenu();

                            }

                        }
                    }
                >

                    <div className="provider-my-menu-modal">


                        {/* =====================================
                            HEADER
                        ===================================== */}

                        <div className="provider-my-menu-header">

                            <div className="provider-my-menu-title">

                                <div className="provider-my-menu-icon">
                                    <MdRestaurantMenu />
                                </div>


                                <div>

                                    <span>
                                        HomeFoods
                                    </span>

                                    <h2>
                                        My Menu
                                    </h2>

                                </div>

                            </div>


                            <button
                                type="button"
                                className="provider-my-menu-close"
                                onClick={
                                    handleCloseMyMenu
                                }
                                disabled={
                                    !!deletingMenuId
                                }
                                aria-label="Close menu"
                            >

                                <MdClose />

                            </button>

                        </div>


                        {/* =====================================
                            CONTENT
                        ===================================== */}

                        <div className="provider-my-menu-content">


                            {/* =================================
                                LOADING
                            ================================= */}

                            {loadingMyMenu && (

                                <div className="provider-my-menu-loading">

                                    Loading your menu...

                                </div>

                            )}


                            {/* =================================
                                ERROR
                            ================================= */}

                            {!loadingMyMenu &&
                                myMenuError && (

                                    <div className="provider-my-menu-error">

                                        {myMenuError}

                                    </div>

                                )}


                            {/* =================================
                                EMPTY
                            ================================= */}

                            {!loadingMyMenu &&
                                !myMenuError &&
                                myMenu.length === 0 && (

                                    <div className="provider-my-menu-empty">

                                        <div className="provider-my-menu-empty-icon">
                                            🍽️
                                        </div>

                                        <h3>
                                            No menu items yet
                                        </h3>

                                        <p>
                                            Add your first menu
                                            item using Add Menu.
                                        </p>

                                    </div>

                                )}


                            {/* =================================
                                MENU ITEMS
                            ================================= */}

                            {!loadingMyMenu &&
                                !myMenuError &&
                                myMenu.length > 0 && (

                                    <div className="provider-my-menu-items">

                                        {myMenu.map(
                                            food => {

                                                const finalPrice =
                                                    Number(
                                                        food.offerPrice
                                                    ) > 0
                                                        ? Number(
                                                            food.offerPrice
                                                        )
                                                        : Number(
                                                            food.price
                                                        );


                                                return (

                                                    <div
                                                        key={
                                                            food._id
                                                        }
                                                        className="provider-my-menu-item"
                                                    >


                                                        {/* IMAGE */}

                                                        <div className="provider-my-menu-item-image">

                                                            {food.image ? (

                                                              <img
  src={getImageUrl(food.image)}
  alt={food.foodName || "Food"}
  onError={(event) => {
    event.currentTarget.src =
      "/images/food-placeholder.png";
  }}
/>

                                                            ) : (

                                                                <span>
                                                                    🍽️
                                                                </span>

                                                            )}

                                                        </div>


                                                        {/* INFO */}

                                                        <div className="provider-my-menu-item-info">

                                                            <h3>
                                                                {food.foodName}
                                                            </h3>


                                                            <p>
                                                                {food.description ||
                                                                    food.foodName}
                                                            </p>


                                                            <strong>
                                                                ₹{finalPrice}

                                                                <span>
                                                                    {" "}
                                                                    /{" "}
                                                                    {food.unit ||
                                                                        "item"}
                                                                </span>
                                                            </strong>

                                                        </div>


                                                        {/* DELETE */}

                                                        <button
                                                            type="button"
                                                            className="provider-my-menu-delete"
                                                            onClick={() =>
                                                                handleDeleteMenu(
                                                                    food._id
                                                                )
                                                            }
                                                            disabled={
                                                                deletingMenuId ===
                                                                food._id
                                                            }
                                                            aria-label={
                                                                `Delete ${food.foodName}`
                                                            }
                                                            title="Delete menu item"
                                                        >

                                                            {deletingMenuId ===
                                                            food._id ? (

                                                                <span>
                                                                    ...
                                                                </span>

                                                            ) : (

                                                                <MdDelete />

                                                            )}

                                                        </button>

                                                    </div>

                                                );

                                            }
                                        )}

                                    </div>

                                )}

                        </div>

                    </div>

                </div>

            );

        };


    // =========================================================
    // CHAT
    // =========================================================

    const renderChat =
        () => {

            if (
                !selectedConversation
            ) {

                return renderEmptyChat();

            }


            return (

                <div className="provider-messages-chat">


                    {/* =========================================
                        CHAT HEADER
                    ========================================= */}

                    <ChatHeader
                        conversation={
                            selectedConversation
                        }
                        onBack={
                            handleBack
                        }
                        onCall={
                            handleCall
                        }
                    />


                    {/* =========================================
                        MESSAGE LIST
                    ========================================= */}

                    <div className="provider-messages-list">


                        {loadingMessages && (

                            <div className="provider-messages-loading">

                                Loading messages...

                            </div>

                        )}


                        {!loadingMessages &&
                            messages.length === 0 && (

                                <div className="provider-first-message">

                                    <div>
                                        💬
                                    </div>

                                    <h3>
                                        Start the conversation
                                    </h3>

                                    <p>
                                        Reply to your
                                        customer here.
                                    </p>

                                </div>

                            )}


                        {!loadingMessages &&
                            messages.map(
                                message => (

                                    <Message
                                        key={
                                            message._id
                                        }
                                        message={
                                            message
                                        }
                                        currentUserId={
                                            currentUserId
                                        }
                                    />

                                )
                            )}


                        <div
                            ref={
                                messagesEndRef
                            }
                        />

                    </div>


                    {/* =========================================
                        ADD MENU MODAL
                    ========================================= */}

                    {showAddMenu && (

                        <AddMenuModal
                            onClose={
                                handleCloseMenu
                            }
                            onSave={
                                handleSaveMenu
                            }
                            saving={
                                savingMenu
                            }
                        />

                    )}


                    {/* =========================================
                        MY MENU
                    ========================================= */}

                    {renderMyMenu()}


                    {/* =========================================
                        MESSAGE INPUT
                    ========================================= */}

                    <MessageInput
                        mode="provider"
                        conversation={
                            selectedConversation
                        }
                        onSendMessage={
                            handleSendMessage
                        }
                        onOpenMenu={
                            handleOpenMenu
                        }
                        onOpenMyMenu={
                            handleOpenMyMenu
                        }
                        disabled={
                            selectedConversation.status !==
                            "active"
                        }
                    />

                </div>

            );

        };


    // =========================================================
    // MAIN
    // =========================================================

    return (

        <div
            className={`provider-messages-page ${
                mobileChatOpen
                    ? "provider-mobile-chat-open"
                    : ""
            }`}
        >

            <div className="provider-messages-container">


                {/* =========================================
                    HEADER
                ========================================= */}

                <div className="provider-messages-header">

                    <div>

                        <div className="provider-messages-eyebrow">
 <img
  src="/images/logo.png"
  alt="HomeFoods"
/>
</div>


                        


                        <p>
                            Chat directly with
                            your customers and
                            discuss their orders.
                        </p>

                    </div>

                </div>


                {/* =========================================
                    CONTENT
                ========================================= */}

                <div className="provider-messages-content">


                    {/* =====================================
                        CONVERSATION PANEL
                    ===================================== */}

                    <div className="provider-conversation-panel">

                        {loading ? (

                            <div className="provider-conversation-loading">

                                Loading conversations...

                            </div>

                        ) : error ? (

                            <div className="provider-conversation-error">

                                <p>
                                    {error}
                                </p>


                                <button
                                    type="button"
                                    onClick={
                                        loadConversations
                                    }
                                >
                                    Retry
                                </button>

                            </div>

                        ) : (

                            <ConversationList
                                mode="provider"
                                selectedConversation={
                                    selectedConversation
                                }
                                onSelectConversation={
                                    handleSelectConversation
                                }
                                conversations={
                                    conversations
                                }
                            />

                        )}

                    </div>


                    {/* =====================================
                        CHAT
                    ===================================== */}

                    <div className="provider-messages-chat-wrapper">

                        {renderChat()}

                    </div>

                </div>

            </div>

        </div>

    );

}


export default ProviderMessages;